import React, { useRef } from 'react';
import { gsap, useGSAP } from '../../lib/gsap';

/** Subtle pointer-driven 3D tilt with a moving glare. Fine pointers only. */
const Tilt = ({ children, className = '', max = 6 }: { children: React.ReactNode; className?: string; max?: number }) => {
   const el = useRef<HTMLDivElement>(null);
   const glare = useRef<HTMLDivElement>(null);

   useGSAP(
      () => {
         const node = el.current;
         if (!node || !window.matchMedia('(pointer: fine)').matches) return;
         gsap.set(node, { transformPerspective: 900 });
         const rx = gsap.quickTo(node, 'rotationX', { duration: 0.6, ease: 'power3.out' });
         const ry = gsap.quickTo(node, 'rotationY', { duration: 0.6, ease: 'power3.out' });

         const move = (e: PointerEvent) => {
            const r = node.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width;
            const py = (e.clientY - r.top) / r.height;
            ry((px - 0.5) * max * 2);
            rx(-(py - 0.5) * max * 2);
            if (glare.current) {
               glare.current.style.background = `radial-gradient(420px circle at ${px * 100}% ${py * 100}%, rgba(255,255,255,0.09), transparent 45%)`;
            }
         };
         const leave = () => {
            rx(0);
            ry(0);
            if (glare.current) glare.current.style.background = 'transparent';
         };
         node.addEventListener('pointermove', move);
         node.addEventListener('pointerleave', leave);
         return () => {
            node.removeEventListener('pointermove', move);
            node.removeEventListener('pointerleave', leave);
         };
      },
      { scope: el }
   );

   return (
      <div ref={el} className={`relative will-change-transform [transform-style:preserve-3d] ${className}`}>
         {children}
         <div ref={glare} className="pointer-events-none absolute inset-0 z-10 rounded-[inherit] transition-[background] duration-300" />
      </div>
   );
};

export default Tilt;
